import { queriesPayStatements, queriesPayables, queriesWorkLogs } from "@query";
import {
  WorkLog,
  WorkLogConvertedResourceType
} from "@wingspanhq/wingspan-api-sdk";
import { useCallback } from "react";

/**
 * Cancels whatever an approved work log converted into, so the work log itself
 * can be reopened or rejected.
 *
 * The resource differs by engagement type and so does the endpoint: a W-2 work
 * log converts into a **pay statement** (`cancelPayStatement`), a 1099 one into
 * a **payable** (`cancelPayable`). Both ids live on the same
 * `convertedResourceId` field, so calling the payable endpoint for an employee
 * work log sends a pay-statement id to `/payables/{id}/cancel` — which is what
 * made "Cancel pay statement" and "Reopen for changes" fail on the employee
 * side.
 *
 * A work log with no converted resource (a discarded one, or an approval that
 * never produced a statement) resolves as a no-op success: there is nothing to
 * cancel, and the caller's follow-up work-log transition still has to run.
 */
export const useMutationCancelWorkLogConvertedResource = () => {
  // Cancelling detaches the work log from the resource, so the work-log reads
  // have to refresh alongside the payable/statement reads the registry already
  // invalidates by default.
  const dependencies = [
    queriesWorkLogs.getWorkLog.queryKeyBase,
    queriesWorkLogs.listWorkLogs.queryKeyBase
  ];

  const [cancelPayable, cancelPayableMeta] =
    queriesPayables.cancelPayable.useMutation({ dependencies });

  const [cancelPayStatement, cancelPayStatementMeta] =
    queriesPayStatements.cancelPayStatement.useMutation({ dependencies });

  const cancelConvertedResource = useCallback(
    (workLog: WorkLog, reason?: string): Promise<boolean> => {
      const convertedResourceId = workLog.convertedResourceId ?? undefined;

      if (!convertedResourceId) {
        return Promise.resolve(true);
      }

      if (
        workLog.convertedResourceType ===
        WorkLogConvertedResourceType.PAY_STATEMENT
      ) {
        // TODO(v3): `cancelPayStatement` takes no request body, so the public
        // comment collected by the modal is not persisted on the employee side.
        return new Promise(resolve => {
          cancelPayStatement(
            { payStatementId: convertedResourceId },
            { onSuccess: () => resolve(true), onError: () => resolve(false) }
          );
        });
      }

      return new Promise(resolve => {
        cancelPayable(
          {
            payableId: convertedResourceId,
            voidInvoiceRequest: reason ? { reason } : undefined
          },
          { onSuccess: () => resolve(true), onError: () => resolve(false) }
        );
      });
    },
    [cancelPayStatement, cancelPayable]
  );

  return {
    cancelConvertedResource,
    isLoading: cancelPayableMeta.isLoading || cancelPayStatementMeta.isLoading,
    error: cancelPayableMeta.error ?? cancelPayStatementMeta.error
  };
};
